import {EventBus} from "../../../../../../common/ts/events/EventBus";
import {WebPushSearchAddedEvent, WebPushPermissionsEventType, WebPushPermissionsUpdatedEvent} from "./events";
import eventHandlers from "./eventHandlers";
import {TrovitSerpData} from "../../../TrovitPageData";
import {isDesktopDevice, isMobileDevice} from "../../../../device";
import {WebPushStorage} from "./model/webPushStorage";
import {
    GoogleOneTapCloseEvent,
    GoogleOneTapFlowEvent,
    PointOfCaptureExitEvent,
    UserHasNotInteractedEvent,
    WebPushNotificationDeniedEvent,
    WebPushNotificationGrantedEvent,
    WhitelabelEvent
} from "../../../../../../common/ts/events/Events";
import TrovitPathPrefixPreponder from "../../../../core/url/TrovitPathPrefixPreponder";

const webPushStorage = new WebPushStorage();

let eventBus: EventBus = EventBus.getInstance();

async function init(pageData: TrovitSerpData) {
    if ('serviceWorker' in navigator) {
        eventHandlers.init();
        if (
            isDesktopDevice(pageData.device)
        ) {
            eventBus.subscribeWithId(
                GoogleOneTapCloseEvent.TYPE,
                {
                    id: `${GoogleOneTapCloseEvent.TYPE}-handler-wpn-desktop-close`,
                    callback: registerDesktopServiceWorkerByClose(pageData)
                }
            )
            eventBus.subscribeWithId(PointOfCaptureExitEvent.TYPE, {
                id: `${PointOfCaptureExitEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as PointOfCaptureExitEvent);
                    if (flow == GoogleOneTapFlowEvent.SIGN_IN) {
                        await registerDesktopServiceWorker(pageData, flow);
                    }
                }
            });
            eventBus.subscribeWithId(UserHasNotInteractedEvent.TYPE, {
                id: `${UserHasNotInteractedEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as UserHasNotInteractedEvent);
                    await registerDesktopServiceWorker(pageData, flow);
                }
            });
        } else if (
            isMobileDevice(pageData.device)
        ) {
            eventBus.subscribeWithId(
                GoogleOneTapCloseEvent.TYPE,
                {
                    id: `${GoogleOneTapCloseEvent.TYPE}-handler-wpn-mobile-close`,
                    callback: registerMobileServiceWorkerByClose(pageData)
                }
            )
            eventBus.subscribeWithId(PointOfCaptureExitEvent.TYPE, {
                id: `${PointOfCaptureExitEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as PointOfCaptureExitEvent);
                    if (flow == GoogleOneTapFlowEvent.SIGN_IN) {
                        await registerMobileServiceWorker(pageData, flow);
                    }
                }
            });
            eventBus.subscribeWithId(UserHasNotInteractedEvent.TYPE, {
                id: `${UserHasNotInteractedEvent.TYPE}-handler-wpn`,
                callback: async (event: WhitelabelEvent) => {
                    const {payload: {flow}} = (event as UserHasNotInteractedEvent);
                    await registerMobileServiceWorker(pageData, flow);
                }
            });
        }
    }
}

async function registerMobileServiceWorker(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    const serviceWorkerPath = TrovitPathPrefixPreponder.prependVerticalPrefixToPath('/service-worker.js');
    await navigator.serviceWorker.register(serviceWorkerPath);
    await checkPermissions(pageData, flow);
}

function registerMobileServiceWorkerByClose(pageData: TrovitSerpData) {
    return async (event: WhitelabelEvent) => {
        const {payload: {flow}} = (event as GoogleOneTapCloseEvent);
        await registerMobileServiceWorker(pageData, flow);
    };
}

async function registerDesktopServiceWorker(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    const serviceWorkerPath = TrovitPathPrefixPreponder.prependVerticalPrefixToPath('/service-worker.desktop.js');
    await navigator.serviceWorker.register(serviceWorkerPath);
    await checkPermissions(pageData, flow);
}

function registerDesktopServiceWorkerByClose(pageData: TrovitSerpData) {
    return async (event: WhitelabelEvent) => {
        const {payload: {flow}} = (event as GoogleOneTapCloseEvent);
        await registerDesktopServiceWorker(pageData, flow);
    }
}

async function checkPermissions(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    if (isBrowserCompatibleWithWebPush()) {
        await handlePermissions(pageData, flow);
    }
}

async function handlePermissions(pageData: TrovitSerpData, flow: GoogleOneTapFlowEvent) {
    if (Notification.permission === 'default') {
        await emitWebPushPermissionsUpdated(WebPushPermissionsEventType.REQUEST, pageData);
        const permission = await Notification.requestPermission();
        if (permission === 'default') {
            await emitWebPushPermissionsUpdated(WebPushPermissionsEventType.CLOSE, pageData);
        } else if (permission === 'granted') {
            await emitWebPushPermissionsUpdated(WebPushPermissionsEventType.ALLOW, pageData);
            await checkSubscribeToWebPush(pageData);
            emitWebPushGranted(flow);
        } else {
            await emitWebPushPermissionsUpdated(WebPushPermissionsEventType.DISALLOW, pageData);
            emitWebPushDenied(flow);
        }
    } else if (Notification.permission === 'granted') {
        await checkSubscribeToWebPush(pageData);
        emitWebPushGranted(flow);
    } else if (Notification.permission === 'denied') {
        emitWebPushDenied(flow);
    }
}

function emitWebPushGranted(flow: GoogleOneTapFlowEvent) {
    eventBus.emit(new WebPushNotificationGrantedEvent({ flow: flow }));
}

function emitWebPushDenied(flow: GoogleOneTapFlowEvent) {
    eventBus.emit(new WebPushNotificationDeniedEvent({ flow: flow }));
}

async function checkSubscribeToWebPush(pageData: TrovitSerpData) {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    let subscription = await serviceWorkerRegistration.pushManager.getSubscription();
    if (!subscription) {
        subscription = await subscribeToWebPush();
    }

    if (subscription && webPushStorage.getSearch(pageData.what) === undefined) {
        eventBus.emit(new WebPushSearchAddedEvent({search: pageData, subscription}));
    }
}

async function subscribeToWebPush() {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    return await serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array('BJFfIhYJIx1qtxNbwycJl7RMks_lf1oB38U7u5d70LWdQ0aZHj75RGy_BCwg-ADPPaQoFFsKmadoDiIf-ATQPac'),
    })
}

async function emitWebPushPermissionsUpdated(event: WebPushPermissionsEventType, pageData: TrovitSerpData){
    var userId = '';
    if (event == WebPushPermissionsEventType.ALLOW && webPushStorage.getPushUserId() !== '') {
        userId = webPushStorage.getPushUserId();
    }
    eventBus.emit(new WebPushPermissionsUpdatedEvent({ pageData: pageData, userId: userId, eventType: event}));
}

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

function isBrowserCompatibleWithWebPush(): boolean {
    return 'PushManager' in window && 'showNotification' in ServiceWorkerRegistration.prototype;
}

export default {
    init,
};
